// CreatureRig — modular DISPATCHER (iter31 architecture upgrade).
//   Reads the species spec from /lib/species_registry.js and delegates to
//   the appropriate renderer:
//     - spec.model.type === 'glb'        → GLBRenderer (loads .glb + clips)
//     - spec.model.type === 'procedural' → this file's low-poly archetypes
//
//   When professional rigged assets arrive, only the species registry
//   entry changes — this file, CinematicScene, camera, HUD, and the
//   battle engine all stay untouched. That's the contract.
import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import GLBRenderer from './renderers/GLBRenderer';
import { resolveSpecies, worldScaleFor } from '../../lib/species_registry';

export default function CreatureRig({ side, snap, position, facing, hp, lastLineKind, cursor, auraColor }) {
  const group = useRef();
  const body = useRef();
  const spec = resolveSpecies(snap);
  const alive = hp > 0;

  // Action pulse — driven by cursor & lastLineKind. When the reducer advances
  // we snapshot the intent and animate for ~0.5 s. This state is passed to
  // BOTH the procedural rig AND (in future) the GLB animation controller.
  const action = useRef({ kind: 'idle', t: 0, until: 0 });
  const prevCursor = useRef(cursor);

  useFrame((_, dt) => {
    const now = performance.now();
    if (cursor !== prevCursor.current) {
      const relevant = actionForSide(side, lastLineKind);
      if (relevant) action.current = { kind: relevant, t: 0, until: now + 500 };
      prevCursor.current = cursor;
    }
    const g = group.current;
    if (!g) return;

    // Species-aware world scaling — real height in metres → world units.
    const worldScale = worldScaleFor(spec);
    const idleY = Math.sin(now * 0.002 + (side === 'ch' ? 0 : 1.6)) * 0.06;
    let px = position[0], py = position[1], pz = position[2];
    let rotY = facing < 0 ? Math.PI : 0;
    let extraScale = 1;

    if (!alive) {
      g.position.set(px, py - 0.35, pz);
      g.rotation.set(-0.55 * facing, rotY, 0);
      g.scale.setScalar(worldScale);
      return;
    }

    const t = (now - (action.current.until - 500)) / 500;
    const progress = THREE.MathUtils.clamp(t, 0, 1);
    switch (action.current.kind) {
      case 'attack':   px += Math.sin(progress * Math.PI) * 1.4 * -facing;
                       rotY += 0.15 * -facing * Math.sin(progress * Math.PI); break;
      case 'crit':     px += Math.sin(progress * Math.PI) * 1.8 * -facing;
                       py += Math.sin(progress * Math.PI) * 0.35;
                       extraScale = 1 + Math.sin(progress * Math.PI) * 0.09; break;
      case 'dodge':    pz += Math.sin(progress * Math.PI) * 0.8 * (side === 'ch' ? -1 : 1);
                       rotY += 0.35 * Math.sin(progress * Math.PI) * facing; break;
      case 'flinch':   py += Math.sin(progress * Math.PI * 3) * 0.08;
                       px += Math.sin(progress * Math.PI * 4) * 0.05 * facing; break;
      case 'ultimate': py += Math.sin(progress * Math.PI) * 0.45;
                       extraScale = 1 + progress * 0.15; break;
      default:         py += idleY;
    }

    g.position.set(px, py, pz);
    g.rotation.set(0, rotY, 0);
    g.scale.setScalar(worldScale * extraScale);

    // Aura pulse from consumables (challenger only, procedural rig only).
    if (body.current?.material && auraColor && side === 'ch') {
      body.current.material.emissive.set(auraColor);
      body.current.material.emissiveIntensity = 0.4 + Math.sin(now * 0.006) * 0.15;
    } else if (body.current?.material) {
      body.current.material.emissiveIntensity = Math.max(0, (body.current.material.emissiveIntensity || 0) - dt * 2);
    }
  });

  const isGLB = spec.model?.type === 'glb' && spec.model?.url;

  return (
    <group ref={group} position={position} name={`species-${spec.common_name}`}>
      {isGLB ? (
        <GLBRenderer spec={spec} action={action.current.kind} facing={facing} />
      ) : (
        <ProceduralBody archetype={spec.archetype} materials={spec.materials} bodyRef={body} />
      )}
      {/* Shadow disc — species-scaled to match the rig footprint */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.01, 0]}>
        <circleGeometry args={[Math.max(0.5, spec.size.length_m * 0.35), 24]} />
        <meshBasicMaterial color="black" transparent opacity={0.35} />
      </mesh>
    </group>
  );
}


function actionForSide(side, kind) {
  if (!kind) return null;
  if (kind === 'attack') return 'attack';
  if (kind === 'crit')   return 'crit';
  if (kind === 'dodge')  return 'dodge';
  if (kind === 'combo')  return 'attack';
  if (kind === 'counter') return 'attack';
  if (kind === 'miss')   return 'dodge';
  if (kind === 'ultimate') return 'ultimate';
  if (kind === 'flavor') return null;
  return 'flinch';
}


/* ------------- Procedural archetype bodies (placeholders) ------------- */

function ProceduralBody({ archetype, materials, bodyRef }) {
  switch (archetype) {
    case 'mammal_bear': return <BearBody bodyRef={bodyRef} materials={materials} />;
    case 'raptor':      return <RaptorBody bodyRef={bodyRef} materials={materials} />;
    case 'bird':        return <BirdBody bodyRef={bodyRef} materials={materials} />;
    case 'reptile':     return <ReptileBody bodyRef={bodyRef} materials={materials} />;
    case 'fish':        return <FishBody bodyRef={bodyRef} materials={materials} />;
    case 'insect':      return <InsectBody bodyRef={bodyRef} materials={materials} />;
    case 'plant':       return <PlantBody bodyRef={bodyRef} materials={materials} />;
    case 'mammal_quadruped':
    default:            return <QuadrupedBody bodyRef={bodyRef} materials={materials} />;
  }
}

function QuadrupedBody({ bodyRef, materials }) {
  const c = materials.primary, c2 = materials.secondary || darker(materials.primary);
  return (
    <group>
      <mesh ref={bodyRef} castShadow position={[0, 0.55, 0]}>
        <capsuleGeometry args={[0.42, 0.7, 6, 12]} />
        <meshStandardMaterial color={c} roughness={0.68} />
      </mesh>
      <mesh castShadow position={[0.65, 0.75, 0]}>
        <sphereGeometry args={[0.32, 16, 12]} />
        <meshStandardMaterial color={c} roughness={0.7} />
      </mesh>
      {[[0.72, 1.02, 0.16], [0.72, 1.02, -0.16]].map((p, i) => (
        <mesh key={i} position={p}>
          <coneGeometry args={[0.09, 0.22, 6]} />
          <meshStandardMaterial color={c} roughness={0.7} />
        </mesh>
      ))}
      {[[-0.28, 0.2, 0.18], [-0.28, 0.2, -0.18], [0.28, 0.2, 0.18], [0.28, 0.2, -0.18]].map((p, i) => (
        <mesh key={i} castShadow position={p}>
          <cylinderGeometry args={[0.09, 0.09, 0.4, 6]} />
          <meshStandardMaterial color={c2} roughness={0.75} />
        </mesh>
      ))}
      <mesh position={[-0.55, 0.6, 0]} rotation={[0, 0, 0.4]}>
        <coneGeometry args={[0.11, 0.55, 6]} />
        <meshStandardMaterial color={c} roughness={0.7} />
      </mesh>
    </group>
  );
}

function BearBody({ bodyRef, materials }) {
  const c = materials.primary, c2 = materials.secondary || darker(materials.primary);
  return (
    <group>
      <mesh ref={bodyRef} castShadow position={[0, 0.65, 0]}>
        <capsuleGeometry args={[0.55, 0.9, 6, 12]} />
        <meshStandardMaterial color={c} roughness={0.85} />
      </mesh>
      <mesh castShadow position={[0.75, 0.85, 0]}>
        <sphereGeometry args={[0.42, 16, 12]} />
        <meshStandardMaterial color={c} roughness={0.85} />
      </mesh>
      {[[0.9, 1.15, 0.2], [0.9, 1.15, -0.2]].map((p, i) => (
        <mesh key={i} position={p}>
          <sphereGeometry args={[0.11, 8, 8]} />
          <meshStandardMaterial color={c2} roughness={0.85} />
        </mesh>
      ))}
      {[[-0.32, 0.22, 0.22], [-0.32, 0.22, -0.22], [0.32, 0.22, 0.22], [0.32, 0.22, -0.22]].map((p, i) => (
        <mesh key={i} castShadow position={p}>
          <cylinderGeometry args={[0.13, 0.15, 0.44, 6]} />
          <meshStandardMaterial color={c2} roughness={0.85} />
        </mesh>
      ))}
    </group>
  );
}

function BirdBody({ bodyRef, materials }) {
  const c = materials.primary, acc = materials.accent || '#e0c37a';
  return (
    <group>
      <mesh ref={bodyRef} castShadow position={[0, 0.85, 0]}>
        <sphereGeometry args={[0.35, 16, 12]} />
        <meshStandardMaterial color={c} roughness={0.55} />
      </mesh>
      <mesh castShadow position={[0.42, 1.05, 0]}>
        <sphereGeometry args={[0.2, 12, 10]} />
        <meshStandardMaterial color={c} roughness={0.55} />
      </mesh>
      <mesh position={[0.6, 1.02, 0]}>
        <coneGeometry args={[0.07, 0.24, 6]} />
        <meshStandardMaterial color={acc} roughness={0.4} />
      </mesh>
      {[0.42, -0.42].map((z, i) => (
        <mesh key={i} position={[0, 0.9, z]} rotation={[i ? -0.25 : 0.25, 0, 0.55]}>
          <boxGeometry args={[0.6, 0.06, 0.36]} />
          <meshStandardMaterial color={darker(c)} roughness={0.6} />
        </mesh>
      ))}
      {[0.14, -0.14].map((z, i) => (
        <mesh key={i} position={[0, 0.32, z]}>
          <cylinderGeometry args={[0.045, 0.045, 0.5, 6]} />
          <meshStandardMaterial color={acc} />
        </mesh>
      ))}
    </group>
  );
}

function RaptorBody({ bodyRef, materials }) {
  const c = materials.primary, acc = materials.accent || '#e0c37a', head = materials.secondary || '#f5f0e8';
  return (
    <group>
      <mesh ref={bodyRef} castShadow position={[0, 0.75, 0]}>
        <capsuleGeometry args={[0.32, 0.5, 6, 12]} />
        <meshStandardMaterial color={c} roughness={0.55} />
      </mesh>
      <mesh castShadow position={[0.42, 1.08, 0]}>
        <sphereGeometry args={[0.22, 12, 10]} />
        <meshStandardMaterial color={head} roughness={0.5} />
      </mesh>
      <mesh position={[0.62, 1.02, 0]}>
        <coneGeometry args={[0.08, 0.3, 6]} />
        <meshStandardMaterial color={acc} roughness={0.4} />
      </mesh>
      {/* Big wings for raptors */}
      {[0.62, -0.62].map((z, i) => (
        <mesh key={i} position={[0, 0.8, z]} rotation={[i ? -0.4 : 0.4, 0, 0.85]}>
          <boxGeometry args={[1.1, 0.06, 0.5]} />
          <meshStandardMaterial color={darker(c)} roughness={0.55} />
        </mesh>
      ))}
      {[0.16, -0.16].map((z, i) => (
        <mesh key={i} position={[0, 0.32, z]}>
          <cylinderGeometry args={[0.05, 0.05, 0.5, 6]} />
          <meshStandardMaterial color={acc} />
        </mesh>
      ))}
    </group>
  );
}

function ReptileBody({ bodyRef, materials }) {
  const c = materials.primary, c2 = materials.secondary || darker(materials.primary);
  return (
    <group>
      {[0, 1, 2, 3].map((i) => (
        <mesh key={i} ref={i === 1 ? bodyRef : null} castShadow position={[0.5 * (i - 1.5), 0.22, 0]}>
          <sphereGeometry args={[0.28, 12, 10]} />
          <meshStandardMaterial color={i % 2 ? c2 : c} roughness={0.6} />
        </mesh>
      ))}
      <mesh castShadow position={[1.05, 0.24, 0]}>
        <boxGeometry args={[0.6, 0.24, 0.4]} />
        <meshStandardMaterial color={c} roughness={0.55} />
      </mesh>
      <mesh position={[-1.15, 0.2, 0]}>
        <coneGeometry args={[0.14, 0.8, 6]} />
        <meshStandardMaterial color={c} roughness={0.6} />
      </mesh>
    </group>
  );
}

function FishBody({ bodyRef, materials }) {
  const c = materials.primary;
  return (
    <group rotation={[0, 0, 0.1]} position={[0, 0.9, 0]}>
      <mesh ref={bodyRef} castShadow>
        <sphereGeometry args={[0.5, 16, 12]} />
        <meshStandardMaterial color={c} roughness={0.35} metalness={0.35} />
      </mesh>
      <mesh position={[-0.6, 0, 0]}>
        <coneGeometry args={[0.32, 0.45, 6]} />
        <meshStandardMaterial color={darker(c)} roughness={0.4} metalness={0.3} />
      </mesh>
    </group>
  );
}

function InsectBody({ bodyRef, materials }) {
  const c = materials.primary;
  return (
    <group>
      <mesh ref={bodyRef} castShadow position={[0, 0.35, 0]}>
        <capsuleGeometry args={[0.24, 0.55, 4, 8]} />
        <meshStandardMaterial color={c} roughness={0.55} metalness={0.4} />
      </mesh>
      <mesh castShadow position={[0.45, 0.36, 0]}>
        <sphereGeometry args={[0.18, 12, 10]} />
        <meshStandardMaterial color={darker(c)} roughness={0.55} metalness={0.5} />
      </mesh>
    </group>
  );
}

function PlantBody({ bodyRef, materials }) {
  const c = materials.primary;
  return (
    <group>
      <mesh castShadow position={[0, 0.45, 0]}>
        <cylinderGeometry args={[0.18, 0.28, 0.9, 8]} />
        <meshStandardMaterial color="#5d4128" roughness={0.9} />
      </mesh>
      <mesh ref={bodyRef} castShadow position={[0, 1.15, 0]}>
        <sphereGeometry args={[0.55, 16, 12]} />
        <meshStandardMaterial color={c} roughness={0.62} />
      </mesh>
    </group>
  );
}

function darker(hex, k = 0.7) {
  const c = new THREE.Color(hex).multiplyScalar(k);
  return `#${c.getHexString()}`;
}

